import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { TrendingUp, TrendingDown, ArrowUpRight } from "lucide-react";
import { useMemo } from "react";
import { Account, Transaction } from "@/lib/types";
import { useCurrency } from "@/hooks/useCurrency";

interface NetWorthCardProps {
  accounts: Account[];
  /** Used to compute previous-month net cash flow for MoM % change */
  transactions?: Transaction[];
}

export default function NetWorthCard({ accounts, transactions = [] }: NetWorthCardProps) {
  const { fmt } = useCurrency();

  const netWorth = useMemo(() => {
    return accounts.reduce((total, account) => total + account.balance, 0);
  }, [accounts]);

  // [T40P0] Real MoM change: estimate prior-month net worth by subtracting
  // the current month's net cash flow from the current net worth.
  const { changePercentage, isPositive, hasPriorData } = useMemo(() => {
    const now = new Date();
    const curYear = now.getFullYear();
    const curMonth = now.getMonth();

    const currentMonthIncome = transactions
      .filter((t) => {
        const d = new Date(t.date);
        return t.type === 'income' && d.getFullYear() === curYear && d.getMonth() === curMonth;
      })
      .reduce((s, t) => s + t.amount, 0);

    const currentMonthExpenses = transactions
      .filter((t) => {
        const d = new Date(t.date);
        return t.type === 'expense' && d.getFullYear() === curYear && d.getMonth() === curMonth;
      })
      .reduce((s, t) => s + t.amount, 0);

    const netCashFlow = currentMonthIncome - currentMonthExpenses;
    const estimatedPrior = netWorth - netCashFlow;
    const hasPriorData = currentMonthIncome > 0 || currentMonthExpenses > 0;

    const changeAmt = netWorth - estimatedPrior;
    const pct = estimatedPrior !== 0
      ? ((changeAmt / Math.abs(estimatedPrior)) * 100).toFixed(1)
      : '0.0';
    return { changePercentage: pct, isPositive: changeAmt >= 0, hasPriorData };
  }, [netWorth, transactions]);

  return (
    <Card
      data-section="net-worth"
      data-testid="home-card-kpi"
      className="h-full min-h-0 flex flex-col transition-all duration-200 hover:shadow-md hover:border-primary/20 glass-panel"
    >
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2 shrink-0">
        <CardTitle className="text-sm font-medium">Net Worth</CardTitle>
        <div className="p-2 bg-primary/10 rounded-full">
          <ArrowUpRight className="h-4 w-4 text-primary" />
        </div>
      </CardHeader>
      <CardContent className="flex-1 min-h-0 overflow-hidden">
        <div className="text-lg sm:text-2xl font-bold truncate">
          {fmt(netWorth)}
        </div>
        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
          {hasPriorData ? (
            <>
              {isPositive ? (
                <TrendingUp className="h-3 w-3 text-green-500" />
              ) : (
                <TrendingDown className="h-3 w-3 text-red-500" />
              )}
              <span className={isPositive ? "text-green-500" : "text-red-500"}>
                {isPositive ? "+" : ""}
                {changePercentage}%
              </span>
              <span>from last month</span>
            </>
          ) : (
            <span>No previous month data</span>
          )}
        </p>
      </CardContent>
    </Card>
  );
}
